/**
 * 操作日志的「动作」列怎么写给人看。
 *
 * 日志里的 action 有两种来源：
 * - 前端 / 服务端点名记的动作（`config.verPublish`、`devPanel.setBalance`）：词条是 `audit.<action>`
 * - 服务端写接口统一留痕记的「方法 + 路由」（`PUT /admin/api/config/versions/:v/spin`）：
 *   拆成动词（保存 / 删除…）+ 页面名，页面名尽量借侧栏的叫法（nav.*），三种语言跟着走
 *
 * 路由表漏了新接口时 `audit-label.test.ts` 会红（它把 src/admin 里所有写接口扫一遍）。
 */
import type { MsgKey } from "./i18n";

export type ActionLabel =
  | { kind: "named"; key: MsgKey; rest?: string }
  | { kind: "route"; verb: MsgKey; res: MsgKey | null; raw: string };

/** 按动作整条认（版本发布这类有专门叫法的接口，不拆成「提交 · 某页」） */
const ROUTE_ACTIONS: Array<[RegExp, MsgKey]> = [
  [/^POST config\/versions\/:v\/publish$/, "audit.config.verPublish"],
  [/^POST config\/versions\/:v\/activate$/, "audit.route.verActivate"],
  [/^POST config\/versions\/:v\/module-restore$/, "module.record"],
  [/^POST mcp-feedback\/:id\/resolve$/, "audit.route.mcpFeedbackResolve"],
  [/^POST config\/versions\/:v\/module-import$/, "module.copyRecord"],
  [/^POST config\/versions\/:v\/undo$/, "undo.record"],
  [/^POST config\/versions\/:v\/unfreeze$/, "audit.route.verUnfreeze"],
  [/^POST config\/versions\/:v\/restore$/, "audit.route.verRestore"],
  [/^POST config\/versions$/, "audit.config.verDraft"],
  [/^DELETE config\/versions\/:v$/, "audit.route.verDelete"],
  [/^POST config\/drafts\/prune$/, "audit.route.draftsPrune"],
  [/^POST config\/versions\/:v\/auctions\/new-period$/, "audit.config.aucAddPeriod"],
  [/^POST asset-sign\/backfill$/, "audit.assetSign.backfill"],
  [/^POST dev-token\/sign$/, "audit.devToken.sign"],
  [/^POST (login|login\/token|session\/exchange|session\/renew|third\/[^/]+)$/, "audit.route.login"],
  [/^POST password$/, "audit.route.password"],
  [/^POST assets$/, "audit.route.upload"],
  [/^POST assets\/thumbnails$/, "audit.route.upload"],
  // 视频托管（Cloudflare Stream）开 tus 上传地址（src/admin/stream.ts）
  [/^POST stream\/uploads$/, "audit.route.streamUpload"],
  // 离线推送服务（中台 web-push-service）的超管试发口（src/push/offline-service.ts）
  [/^POST push\/offline\/send$/, "audit.route.pushOfflineSend"],
];

/** 资源 → 页面名。按路径前缀认，先长后短 */
const RESOURCES: Array<[RegExp, MsgKey]> = [
  // 版本内的配置块
  [/^config\/versions\/:v\/levels\/:level\/cells\/:b\/:star\/asset/, "vlog.f.assets"],
  [/^config\/versions\/:v\/levels\/:level\/cells/, "vlog.f.sys"],
  [/^config\/versions\/:v\/levels/, "nav.level"],
  [/^config\/versions\/:v\/cells/, "vlog.f.sys"],
  [/^config\/versions\/:v\/common/, "audit.res.common"],
  [/^config\/versions\/:v\/ability-cards/, "nav.ability-card"],
  [/^config\/versions\/:v\/adopt-pools/, "audit.res.adoptPools"],
  [/^config\/versions\/:v\/auctions/, "nav.auction"],
  [/^config\/versions\/:v\/battle-stages/, "nav.battle-stage"],
  [/^config\/versions\/:v\/board-cells/, "nav.board-cells"],
  [/^config\/versions\/:v\/card-packs/, "audit.res.cardPacks"],
  [/^config\/versions\/:v\/card-pack-drops/, "nav.card-drop"],
  [/^config\/versions\/:v\/daily-rewards/, "nav.daily-reward"],
  [/^config\/versions\/:v\/frames/, "nav.frame"],
  [/^config\/versions\/:v\/idol-photos/, "audit.res.idolPhotos"],
  [/^config\/versions\/:v\/stage-images/, "audit.res.stageImages"],
  [/^config\/versions\/:v\/stages/, "audit.res.stages"],
  [/^config\/versions\/:v\/idol-vip-photos/, "audit.res.idolVipPhotos"],
  [/^config\/versions\/:v\/idol-vip-videos/, "audit.res.idolVipVideos"],
  [/^config\/versions\/:v\/intimacy/, "nav.idol-intimacy"],
  [/^config\/versions\/:v\/lamp-drops/, "nav.lamp-drops"],
  [/^config\/versions\/:v\/match-modes/, "nav.match-mode"],
  // 退役接口仅保留历史审计标题，后台已无对应配置入口。
  [/^config\/versions\/:v\/multipliers/, "progress.multiplier"],
  [/^config\/versions\/:v\/pet-stars/, "nav.pet-star"],
  [/^config\/versions\/:v\/pets/, "nav.pet-list"],
  [/^config\/versions\/:v\/pettown\/slots/, "nav.pettown-slot"],
  [/^config\/versions\/:v\/pettown\/stars/, "nav.pettown-star"],
  [/^config\/versions\/:v\/puzzle-prices/, "nav.puzzle-price"],
  [/^config\/versions\/:v\/puzzles/, "nav.puzzle-list"],
  [/^config\/versions\/:v\/rewards/, "nav.reward"],
  [/^config\/versions\/:v\/rivals/, "audit.res.rivals"],
  [/^config\/versions\/:v\/social-channels/, "nav.social-channel"],
  [/^config\/versions\/:v\/social-event-types/, "nav.social-event-type"],
  [/^config\/versions\/:v\/spin/, "nav.spin"],
  [/^config\/versions\/:v\/talent-nodes/, "nav.level-talent"],
  [/^config\/versions\/:v\/tiles/, "nav.subgame"],
  // 不随版本的配置
  // 建一个 model 2 草稿（admin/econ-migrate.ts）。记录里只写「新建配置版本」，不露口径换过（leo 2026-10-22）
  [/^config\/econ-migrate/, "audit.res.econMigrate"],
  [/^config\/achievements-day/, "nav.newbie-activity"],
  [/^config\/achievement/, "audit.res.achievements"],
  [/^config\/act-events/, "audit.res.actEvents"],
  [/^config\/act-items/, "nav.act-item"],
  [/^config\/piggies/, "nav.piggy"],
  [/^config\/pay-chains/, "nav.pay-chain"],
  [/^config\/titles/, "audit.res.titles"],
  // 其他
  [/^accounts/, "nav.accounts"],
  // 账号管理里改显示名 / 备注 / 禁用（admin/admin-users.ts）
  [/^admin-users/, "nav.accounts"],
  [/^agents/, "nav.agents"],
  // 服务端埋点总开关（代理管理页顶部，admin/analytics-switch-admin.ts）
  [/^analytics\//, "agents.serverTa.label"],
  [/^asset-info/, "nav.asset-info"],
  [/^debug-trace/, "nav.debug-trace"],
  [/^econ\//, "audit.res.econSwitch"],
  [/^dev-panel/, "nav.dev-panel"],
  // 二合任务成就：tasks 是四个子页共用的一条路由，审计里统一写成成就页；settings 是每日宝箱页与标签管理页共用的一条
  [/^erhe\/settings/, "audit.res.erheSettings"],
  [/^erhe\/tasks/, "nav.erhe-achievement"],
  [/^discord/, "nav.discord-bind"],
  [/^gm/, "nav.gm"],
  [/^hot-updates/, "nav.hot-update"],
  [/^iap/, "nav.iap"],
  [/^idols/, "nav.idol-list"],
  // 播放器自动上报，服务端不留痕（admin/index.ts）；这里只为扫描闸登记
  [/^learning/, "nav.help-rank"],
  // 顶栏「运营后台 MCP」里建 / 撤自己的令牌（admin/mcp-token.ts）
  [/^me\/mcp-tokens/, "mcp.entry"],
  [/^mail/, "nav.mail"],
  [/^name-library/, "audit.res.nameLibrary"],
  // 运营建的 NPC 号名单（admin/npc-accounts.ts）
  [/^npc-accounts/, "audit.res.npcAccounts"],
  [/^players\/:hex\/client-state/, "nav.report-client-state"],
  [/^players\/:userIdHex\/puzzle-hint/, "audit.res.puzzleHint"],
  [/^players\/(cost-sources|:userIdHex\/cost-source)/, "audit.res.costSource"],
  [/^players/, "nav.report-players"],
  [/^player-(authority|recovery|capture-backup|checkpoint)/, "audit.res.playerStorage"],
  [/^reports/, "nav.reports"],
  [/^share-(links|settings)|^invite-settings/, "nav.share-links"],
  [/^push\/events/, "nav.push-events"],
  [/^shop\/items/, "nav.shop"],
  [/^shop\/sections/, "nav.shop-sections"],
  [/^super-admins/, "nav.accounts"],
];

const VERBS: Record<string, MsgKey> = {
  POST: "audit.verb.post", PUT: "audit.verb.put", PATCH: "audit.verb.patch", DELETE: "audit.verb.delete",
};

/** 点名记的动作里带动态段的：按前缀认，后半截原样跟在后面（玩家存储迁移这类运维动作，段名本身就是代号） */
const NAMED_PREFIXES: Array<[string, MsgKey]> = [
  ["player_authority.", "audit.res.playerStorage"],
  ["player_recovery.", "audit.res.playerStorage"],
];

/** `PUT /admin/api/config/versions/:v/spin` → 动词 + 页面；点名动作 → 词条键。认不出的给 raw 让界面原样显示 */
export function describeAction(action: string, has: (key: string) => boolean): ActionLabel | null {
  if (has(`audit.${action}`)) return { kind: "named", key: `audit.${action}` as MsgKey };
  for (const [p, key] of NAMED_PREFIXES) if (action.startsWith(p)) return { kind: "named", key, rest: action.slice(p.length) };
  const m = /^(GET|POST|PUT|PATCH|DELETE) \/admin\/api\/(.*)$/.exec(action);
  if (!m) return null;
  const [, method, path] = m;
  const whole = `${method} ${path}`;
  for (const [re, key] of ROUTE_ACTIONS) if (re.test(whole)) return { kind: "named", key };
  const res = RESOURCES.find(([re]) => re.test(path))?.[1] ?? null;
  return { kind: "route", verb: VERBS[method] ?? "audit.verb.post", res, raw: path };
}

/** 路由表的覆盖面，给用例用：这条写接口能不能写成人话 */
export const routeCovered = (method: string, path: string) => {
  const d = describeAction(`${method.toUpperCase()} ${path}`, () => false);
  return !!d && (d.kind === "named" || d.res != null);
};

/**
 * 「对象」列：写接口留痕的 target 是请求路径（`/admin/api/config/versions/101/spin`），
 * 跟动作列重复，人要看的只是里面那几个 id。按动作里的路由模板把参数抠出来：
 * 版本号写成 v101，其余原样，多个用 · 连。路由没参数（整页保存）给 null，界面写「—」。
 * 不是写接口留痕的（点名动作自己填的 target）原样返回。
 */
export function targetLabel(action: string, target: string): string | null {
  const m = /^(?:POST|PUT|PATCH|DELETE) (\/admin\/api\/\S+)$/.exec(action);
  if (!m || !target.startsWith("/admin/api/")) return target || null;
  const tpl = m[1].split("/"), seg = target.split("/");
  if (tpl.length !== seg.length) return target;
  const out: string[] = [];
  tpl.forEach((p, i) => {
    if (!p.startsWith(":")) return;
    const v = decodeURIComponent(seg[i]);
    out.push(p === ":v" ? `v${v}` : v);
  });
  return out.length ? out.join(" · ") : null;
}
